// kfe-tab-bar.js — bottom tab bar for the two top-level sections.
//
// iOS canon: TabBarViewController.m presents a bottom tab bar. Web V1 keeps
// TWO of its tabs — Learning | Global Playlist — and drops Reports
// (design-doc §2 principle #5 + §3). Active tab label is orange on iOS
// (TabBarViewController.m:309 `[UIColor orangeColor]`), white otherwise.
//
// Tabs are real <a href> links (Learning → "/", Global Playlist →
// "/playlist") so deep-linking + back/forward work via the router's
// link-click interception (router.js onLinkClick). No pushState here.
//
// Visible ≤1024px (phone + tablet). At ≥1024px the desktop top-nav in
// kfe-app-shell takes over and this component is `display: none`. The 1024px
// breakpoint hardcodes --kfe-bp-desktop from tokens.css (CSS vars can't be
// used in @media conditions) — keep them in sync.
//
// Active-state: the shell sets the `path` prop on kfe:route-changed; the
// reactive setter re-renders this (small, sibling-of-<main>) component in
// place — it never touches the routed <main> outlet.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { LEARNING_ICON, PLAYLIST_ICON } from "./icons/tab-icons.js";

/**
 * Which top-level section a route belongs to.
 *   "learning"  → "/", "/stage/*", "/lesson/*", "/exercise/*"
 *   "playlist"  → "/playlist", "/playlist/*"
 *   null        → "/settings", "/login", "/personal-promise" (no active tab)
 * Shared by the tab bar and the shell's top-nav so the two can't drift.
 * @param {string} path
 * @returns {"learning" | "playlist" | null}
 */
export function sectionForPath(path) {
  if (path === "/playlist" || path.startsWith("/playlist/")) return "playlist";
  if (
    path === "/" ||
    path.startsWith("/stage/") ||
    path.startsWith("/lesson/") ||
    path.startsWith("/exercise/")
  ) {
    return "learning";
  }
  return null;
}

/** @param {string} svg @returns {Node} */
function svgNode(svg) {
  const tpl = document.createElement("template");
  tpl.innerHTML = svg.trim();
  return tpl.content.cloneNode(true);
}

export class KFETabBar extends KFEComponent {
  static props = ["path"];

  static styles = `
    /* Bottom tab bar — hidden once the desktop top-nav takes over.
       1024px === --kfe-bp-desktop (tokens.css); keep in sync. */
    :host {
      display: flex;
      border-top: 1px solid var(--kfe-border);
      background: var(--kfe-bg-header);
      padding-bottom: env(safe-area-inset-bottom);
    }
    @media (min-width: 1024px) { :host { display: none; } }

    a {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.15rem;
      padding: 0.5rem 0.25rem;
      text-decoration: none;
      color: var(--kfe-fg);
      font-size: 0.7rem;
      -webkit-tap-highlight-color: transparent;
    }
    a[aria-current="page"] { color: var(--kfe-accent); }

    .icon { display: block; width: 26px; height: 26px; }
    .icon svg { width: 100%; height: 100%; }
    .label { line-height: 1; }
  `;

  render() {
    const path = /** @type {string} */ (/** @type {any} */ (this).path || "/");
    const active = sectionForPath(path);

    const tab = (/** @type {string} */ href, /** @type {"learning"|"playlist"} */ section, /** @type {Node} */ icon, /** @type {string} */ label) => {
      const a = html`
        <a href="${href}">
          <span class="icon">${icon}</span>
          <span class="label">${label}</span>
        </a>
      `;
      if (active === section) {
        const el = /** @type {HTMLAnchorElement|null} */ (a.querySelector("a"));
        if (el) el.setAttribute("aria-current", "page");
      }
      return a;
    };

    return html`
      ${tab("/", "learning", svgNode(LEARNING_ICON), t("nav.learning"))}
      ${tab("/playlist", "playlist", svgNode(PLAYLIST_ICON), t("nav.globalPlaylist"))}
    `;
  }
}
